import { createContext, useContext, useMemo } from "@lukekaalim/act";
import { Observable, Subject } from "rxjs";
import { cyrb64, stringHash } from "./hash";

export const useLogger = (subsystem: string | null = null): Logger => {
  const logger = useContext(LoggerContext);
  
  return useMemo(() => {
    if (subsystem)
      return logger.createSubsystem(subsystem);
    return logger;
  }, [subsystem]);
};

const GLOBAL_LOGGER = {
  log: console.log,
  warn: console.warn,
  error: console.error,
  createSubsystem: () => GLOBAL_LOGGER,
}

export const LoggerContext = createContext<Logger>(GLOBAL_LOGGER);

export type Logger = {
  log(...message: unknown[]): void,
  warn(...message: unknown[]): void,
  error(...message: unknown[]): void,

  createSubsystem(name: string): Logger 
}
export const Logger = {
  noop(): Logger {
    return {
      log() {},
      warn() {},
      error() {},
      createSubsystem() { return Logger.noop() },
    }
  }
}

export type LogStreamEvent = {
  name: string,
  subsystems: string[],
  level: 'log' | 'warn' | 'error',
  message: unknown[],
  trace: string,
}

export type LogStreamProvider = {
  createLogger(name: string): Logger,
  emitter: Observable<LogStreamEvent>,
}

export const createLogStreamProvider = (): LogStreamProvider => {
  const emitter = new Subject<LogStreamEvent>();

  const createLogger = (name: string, subsystems: string[] = []): Logger => {
    return {
      log(...message) {
        // const traceLine = (new Error().stack || '').split('\n')[1];
        // const firstAt = traceLine.indexOf('@');
        // const url = new URL(traceLine.substring(firstAt + 1));
        // const parts = url.pathname.split('/');
        // const path = parts.slice(-4).join('/');

        const trace = '';
        emitter.next({ name, level: 'log', message, trace, subsystems });
      },
      warn(...message) {
        const trace = (new Error().stack || '').split('\n')[1].split('@', 1)[1];
        emitter.next({ name, level: 'warn', message, trace, subsystems });
      },
      error(...message) {
        const trace = (new Error().stack || '').split('\n')[1].split('@', 1)[1];
        emitter.next({ name, level: 'error', message, trace, subsystems });
      },
      createSubsystem(subsystemName) {
        return createLogger(name, [...subsystems, subsystemName])
      },
    }
  }

  return {
    createLogger,
    emitter,
  }
};

export const createConsoleLogStreamRenderer = (logStreamEmitter: Observable<LogStreamEvent>) => {
  const onLogEmit = ({ name, level, message, subsystems, trace }: LogStreamEvent) => {
    const styleSegments = [] as string[];
    const messageSegments = [] as unknown[];

    const pushSegment = (message: string, style: string) => {
      styleSegments.push(style);
      messageSegments.push(`%c${message}`)
    }

    for (const systemName of [name, ...subsystems]) {
      const nameHue = cyrb64(cyrb64(systemName).toString()) / (Number.MAX_SAFE_INTEGER) * 360 * 2;
      const nameColor = `hsl(${nameHue}deg, 50%, 50%)`
      pushSegment(systemName, `background-color: ${nameColor}; color: #fff; font-weight: bold; display: inline-block; padding: 4px;`);
    }

    switch (level) {
      case 'error':
        pushSegment(`Error`, 'background-color: #d11b1b; color: white; font-weight: bold; display: inline-block; padding: 4px;'); break;
      case 'log':
        //pushSegment(`Log`, 'background-color: #d0ecef; color: black; font-weight: bold; display: inline-block; padding: 4px;');
        break;
      case 'warn':
        pushSegment(`Warn`, 'background-color: #e8a93c; color: black; font-weight: bold; display: inline-block; padding: 4px;'); break;
    }

    const args = [
      messageSegments.join(' '),
      ...styleSegments,
      ...message,
      '\t\t' + trace,
    ];
    console.log(...args);
  }

  const sub = logStreamEmitter.subscribe(onLogEmit)

  return {
    close() {
      sub.unsubscribe();
    }
  }
}